import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { ArrowRight, Check, Loader2, Search, Shuffle, Tag, X } from "lucide-react";

import Button from "../ui/Button";
import { cn } from "../../utils/classNames";

export default function ArchiveSearchInput({
  activeSuggestion,
  canSubmitSearch,
  clearSearchInput,
  comboboxId,
  discoveryPrompt,
  inputRef,
  inputValue,
  listboxId,
  onInputChange,
  onKeyDown,
  prefetchIndex,
  placeholder,
  searchStatus,
  selectSuggestion,
  setActiveSuggestion,
  setSuggestionsOpen,
  showSuggestions,
  statusId,
  surpriseMeDisabled,
  surpriseMeLabel,
  onSurpriseMe,
  submitSemantic,
  suggestions,
}) {
  useEffect(() => {
    if (activeSuggestion < 0) return;
    document.getElementById(`${listboxId}-${activeSuggestion}`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeSuggestion, listboxId]);

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="relative">
        <label htmlFor={comboboxId} className="sr-only">
          Search by show or taste
        </label>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary" aria-hidden="true" />
        <input
          ref={inputRef}
          id={comboboxId}
          type="text"
          inputMode="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          aria-controls={listboxId}
          aria-activedescendant={activeSuggestion >= 0 ? `${listboxId}-${activeSuggestion}` : undefined}
          aria-describedby={statusId}
          value={inputValue}
          onChange={onInputChange}
          onFocus={() => {
            prefetchIndex();
            setSuggestionsOpen(true);
          }}
          onPointerEnter={prefetchIndex}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-12 w-full rounded-lg border border-accent-gold/70 bg-background/85 pl-11 pr-11 text-body text-foreground outline-hidden transition focus:border-primary focus:ring-2 focus:ring-primary/25"
        />
        {inputValue && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-2 top-1/2 h-9 min-h-9 w-9 min-w-9 -translate-y-1/2 text-muted-foreground hover:bg-accent-gold/10 hover:text-foreground"
            onClick={clearSearchInput}
            aria-label="Clear archive search"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}

        {showSuggestions && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-30 mt-2 max-h-80 w-full overflow-auto rounded-lg border border-accent-gold/70 bg-card p-2 shadow-xl"
          >
            {suggestions.map((suggestion, indexValue) => {
              const active = indexValue === activeSuggestion;
              const isShowsLike = suggestion.type === "shows-like";
              const Icon = suggestion.type === "tag" ? Tag : isShowsLike ? ArrowRight : Search;
              return (
                <Button
                  key={suggestion.id}
                  id={`${listboxId}-${indexValue}`}
                  type="button"
                  role="option"
                  aria-selected={active}
                  variant="ghost"
                  className={cn(
                    "flex min-h-12 w-full justify-between gap-3 rounded-md px-3 py-2 text-left",
                    isShowsLike && "pl-9",
                    active ? "bg-primary text-primary-foreground" : "hover:bg-accent-gold/10"
                  )}
                  onMouseEnter={() => setActiveSuggestion(indexValue)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className={cn("block truncate text-body-small", isShowsLike ? "font-medium" : "font-semibold")}>{suggestion.label}</span>
                      {suggestion.meta && (
                        <span className={cn("block truncate text-body-xs", active ? "text-primary-foreground/80" : "text-muted-foreground")}>
                          {suggestion.meta}
                        </span>
                      )}
                    </span>
                  </span>
                  {suggestion.selected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
        <Button
          type="button"
          variant="default"
          disabled={!canSubmitSearch || searchStatus === "semantic-loading"}
          onClick={submitSemantic}
          className="min-h-11 w-full sm:w-auto"
        >
          <span className="inline-flex items-center gap-2">
            {searchStatus === "semantic-loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Search className="h-4 w-4" aria-hidden="true" />
            )}
            {discoveryPrompt}
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={surpriseMeDisabled}
          onClick={onSurpriseMe}
          className="min-h-11 w-full sm:w-auto"
        >
          <Shuffle className="h-4 w-4" aria-hidden="true" />
          {surpriseMeLabel}
        </Button>
      </div>
    </div>
  );
}

ArchiveSearchInput.propTypes = {
  activeSuggestion: PropTypes.number.isRequired,
  canSubmitSearch: PropTypes.bool.isRequired,
  clearSearchInput: PropTypes.func.isRequired,
  comboboxId: PropTypes.string.isRequired,
  discoveryPrompt: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({ current: PropTypes.any }),
  inputValue: PropTypes.string.isRequired,
  listboxId: PropTypes.string.isRequired,
  onSurpriseMe: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  prefetchIndex: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  searchStatus: PropTypes.string.isRequired,
  selectSuggestion: PropTypes.func.isRequired,
  setActiveSuggestion: PropTypes.func.isRequired,
  setSuggestionsOpen: PropTypes.func.isRequired,
  showSuggestions: PropTypes.bool.isRequired,
  statusId: PropTypes.string.isRequired,
  surpriseMeDisabled: PropTypes.bool,
  surpriseMeLabel: PropTypes.string.isRequired,
  submitSemantic: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    meta: PropTypes.string,
    selected: PropTypes.bool,
    slug: PropTypes.string,
  })).isRequired,
};
